// Shared Server-Sent Events reader for fetch/ky streaming responses (POST SSE,
// where EventSource can't be used). Yields raw `{ event, data }` frames; callers
// map/validate them into their own typed events.

export interface SseFrame {
  event: string;
  data: string;
}

const parseFrame = (part: string): SseFrame | null => {
  let event = '';
  let data = '';
  for (const line of part.split('\n')) {
    if (line.startsWith('event: ')) event = line.slice(7);
    else if (line.startsWith('data: ')) data = line.slice(6);
  }
  return event && data ? { event, data } : null;
};

export async function* readSseFrames(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<SseFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    // On the final read, flush the decoder (no `stream` flag) so a multi-byte
    // character split across chunks isn't dropped.
    buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    // While streaming, the last element may be a partial frame — keep it
    // buffered. On the final read everything left is complete, so emit it all
    // (covers a trailing frame with no terminating blank line).
    buffer = done ? '' : parts.pop() ?? '';

    for (const part of parts) {
      const frame = parseFrame(part);
      if (frame) yield frame;
    }

    if (done) break;
  }
}
